import React, {
  ChangeEvent,
  FocusEvent,
  HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
  PointerEvent,
} from 'react';
import cl from 'classnames';
import { EnterKeyHint, InputMode } from 'src/interface';
import { goToNextElem, uuidV4 } from 'src/helper';
import styles from './inputWithLabelStyle.module.scss';

type ViewPassIconProps = {
  type: 'text' | 'password';
  viewPass: boolean;
  onPointerDown: (event: PointerEvent<SVGSVGElement>) => void;
  size: 'small' | 'medium';
  autoSize: boolean;
};

const ViewPassIcon = ({
  type,
  viewPass,
  onPointerDown,
  size,
  autoSize,
}: ViewPassIconProps) => {
  if (type === 'text') {
    return null;
  }

  if (viewPass) {
    return (
      <svg
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cl(
          styles.passIcon,
          size === 'small' && styles.small,
          autoSize && styles.autoSize,
        )}
        onPointerDown={onPointerDown}
      >
        <path
          d="M2.80782 13.262C2.67163 13.0464 2.60354 12.9385 2.56542 12.7722C2.53679 12.6473 2.53679 12.4503 2.56542 12.3254C2.60354 12.1591 2.67163 12.0513 2.80782 11.8356C3.93322 10.0537 7.28309 5.54883 12.3881 5.54883C17.4931 5.54883 20.843 10.0537 21.9684 11.8356C22.1046 12.0513 22.1727 12.1591 22.2108 12.3254C22.2394 12.4503 22.2394 12.6473 22.2108 12.7722C22.1727 12.9385 22.1046 13.0464 21.9684 13.262C20.843 15.044 17.4931 19.5488 12.3881 19.5488C7.2831 19.5488 3.93322 15.044 2.80782 13.262Z"
          stroke="#808E9D"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.3881 15.5488C14.045 15.5488 15.3881 14.2057 15.3881 12.5488C15.3881 10.892 14.045 9.54883 12.3881 9.54883C10.7312 9.54883 9.3881 10.892 9.3881 12.5488C9.3881 14.2057 10.7312 15.5488 12.3881 15.5488Z"
          stroke="#808E9D"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cl(
        styles.passIcon,
        size === 'small' && styles.small,
        autoSize && styles.autoSize,
      )}
      onPointerDown={onPointerDown}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.16489 2.79289C3.55541 2.40237 4.18858 2.40237 4.5791 2.79289L8.21783 6.43162C8.27613 6.47793 8.32995 6.53139 8.37795 6.59174L18.769 16.9828C18.8367 17.0347 18.8988 17.0962 18.9529 17.1667L22.5791 20.7929C22.9696 21.1834 22.9696 21.8166 22.5791 22.2071C22.1886 22.5976 21.5554 22.5976 21.1649 22.2071L18.0112 19.0534C16.5663 19.8988 14.8436 20.5 12.872 20.5C10.0103 20.5 7.67734 19.2353 5.94874 17.7926C4.2225 16.3518 3.03896 14.6857 2.44622 13.7472C2.43838 13.7348 2.43034 13.7221 2.42213 13.7092C2.30449 13.524 2.15176 13.2836 2.07456 12.9467C2.01224 12.6748 2.01226 12.325 2.07462 12.053C2.15186 11.7161 2.30503 11.4751 2.42297 11.2895C2.43125 11.2765 2.43935 11.2637 2.44725 11.2512C3.04962 10.2977 4.2729 8.57525 6.06734 7.10955L3.16489 4.20711C2.77437 3.81658 2.77437 3.18342 3.16489 2.79289ZM7.48987 8.53208C5.85069 9.82619 4.70564 11.421 4.13813 12.3194C4.10263 12.3755 4.07775 12.415 4.05695 12.4492C4.04307 12.4721 4.03393 12.4878 4.02783 12.4988C4.02783 12.4992 4.02783 12.4996 4.02783 12.5C4.02783 12.5004 4.02783 12.5008 4.02783 12.5012C4.03385 12.512 4.04285 12.5276 4.05648 12.55C4.07715 12.584 4.10187 12.6232 4.13722 12.6792C4.66988 13.5226 5.72398 14.9999 7.23027 16.2571C8.73421 17.5123 10.6287 18.5 12.872 18.5C14.2249 18.5 15.4494 18.1414 16.539 17.5812L14.9038 15.946C14.3084 16.2977 13.6134 16.5 12.872 16.5C10.6629 16.5 8.872 14.7091 8.872 12.5C8.872 11.7586 9.07429 11.0636 9.42596 10.4682L7.48987 8.53208ZM10.9396 11.9818C10.8955 12.147 10.872 12.3207 10.872 12.5C10.872 13.6046 11.7674 14.5 12.872 14.5C13.0513 14.5 13.225 14.4765 13.3902 14.4324L10.9396 11.9818ZM12.872 6.5C12.4901 6.5 12.1198 6.52849 11.7608 6.58156C11.2144 6.66234 10.7061 6.28492 10.6253 5.73858C10.5445 5.19223 10.9219 4.68385 11.4683 4.60307C11.9221 4.53597 12.3902 4.5 12.872 4.5C15.7337 4.5 18.0667 5.76472 19.7953 7.20743C21.5215 8.64818 22.705 10.3143 23.2978 11.2528C23.3056 11.2652 23.3137 11.2779 23.3219 11.2908C23.4395 11.476 23.5923 11.7164 23.6694 12.0533C23.7318 12.3253 23.7317 12.6751 23.6694 12.9471C23.5921 13.2841 23.4387 13.5254 23.3206 13.7113C23.3122 13.7243 23.3041 13.7371 23.2962 13.7497C22.9778 14.2536 22.4948 14.9581 21.8548 15.7224C21.5002 16.1459 20.8695 16.2017 20.4461 15.8472C20.0226 15.4926 19.9668 14.8619 20.3214 14.4385C20.8927 13.7561 21.324 13.1267 21.6054 12.6814C21.6409 12.6251 21.6659 12.5855 21.6868 12.5512C21.7008 12.5281 21.71 12.5122 21.7162 12.5012C21.7162 12.5008 21.7162 12.5004 21.7162 12.5C21.7162 12.4996 21.7162 12.4992 21.7162 12.4988C21.7101 12.488 21.7011 12.4725 21.6875 12.45C21.6669 12.416 21.6421 12.3768 21.6068 12.3208C21.0741 11.4774 20.02 10.0001 18.5137 8.74291C17.0098 7.4877 15.1153 6.5 12.872 6.5Z"
        fill="#9AA7BB"
      />
    </svg>
  );
};

type InputProps = {
  type?: 'text' | 'password';
  inputMode?: InputMode;
  enterKeyHint?: EnterKeyHint;
  label: string;
  placeholder?: string;
  value: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  autoSize?: boolean;
  size?: 'small' | 'medium';
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: FocusEvent<HTMLInputElement>): void;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
  tabIndex?: number;
  onEnter?(): void;
};

export const InputWithLabel = ({
  type = 'text',
  inputMode = 'text',
  enterKeyHint,
  label,
  placeholder,
  value,
  error,
  disabled = false,
  className,
  size = 'medium',
  autoSize = false,
  onChange,
  onFocus,
  onBlur,
  tabIndex,
  onEnter,
  ...otherProps
}: InputProps & HTMLAttributes<HTMLInputElement>) => {
  const uuid = useMemo(() => uuidV4(), []);
  const [isFocused, setFocused] = useState(false);
  const [viewPass, setViewPass] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const inputType = useMemo(() => {
    if (type === 'text') {
      return type;
    }

    return viewPass ? 'text' : 'password';
  }, [type, viewPass]);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);

      if (onFocus && typeof onFocus === 'function') {
        onFocus(e);
      }
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);

      if (onBlur && typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    [onBlur],
  );

  return (
    <div className={cl(styles.container, className)}>
      <div
        className={cl(
          styles.inputContainer,
          type === 'password' && styles.password,
          error && styles.error,
          disabled && styles.disabled,
        )}
      >
        <label
          htmlFor={`input-with-label-${uuid}`}
          onClick={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className={cl(
            styles.label,
            Boolean(isFocused || value) && styles.active,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
            error && styles.error,
          )}
        >
          {label}
        </label>
        <input
          id={`input-with-label-${uuid}`}
          type={inputType}
          inputMode={inputMode}
          enterKeyHint={enterKeyHint}
          ref={inputRef}
          className={cl(
            styles.input,
            Boolean(isFocused || value) && styles.active,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
          )}
          value={value}
          disabled={disabled}
          placeholder={isFocused ? placeholder : undefined}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete="new-password"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              inputRef.current?.blur();

              if (enterKeyHint === 'next') {
                goToNextElem(tabIndex);
              } else {
                onEnter?.();
              }
            }
          }}
          tabIndex={tabIndex}
          {...otherProps}
        />
        <ViewPassIcon
          type={type}
          viewPass={viewPass}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setViewPass((prevState) => !prevState);
            inputRef.current?.focus();
          }}
          size={size}
          autoSize={autoSize}
        />
      </div>
    </div>
  );
};

export default InputWithLabel;
